import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'

import ActionCard from 'components/Shared/ActionCard'
import { TAResponseProps } from 'types/TextAnalysis'
import { cardUserAndMeta } from './CardListHeader'
import {
  renderCardTitleContent,
  renderCardBodyContent,
  renderCardTitleLink,
} from './CardListContent'
import { renderCardMetricsContent } from './CardListFooter'
import ChipsList from 'components/Shared/ChipsList'
import CardActions from 'components/Shared/ActionCard/CardActions'
import { handleAddTagIcon } from '../Helpers/Helper'
import {
  fetchAllTags,
  getSingleContribution,
  editSentiment,
} from 'redux/thunks/TextAnalysis'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { SingleTagProps, OnDeleteTagProp } from '../Types'
import { handleDeleteTag } from '../Helpers/TagHelper'
import TagMenuContainer from './TagMenuContainer'
import {
  handleCheckboxClickHelper,
  isCardSelected,
} from '../Helpers/BulkSelectHelpers'
import IdeasCommentDialog from './IdeasCommentDialog'
import { Option } from 'components/Shared/SentimentDropdown/SentimentOptions'

const CardList = () => {
  const dispatch = useAppDispatch()
  const { data, selectedContributions: selectedCards } = useAppSelector(
    (state) => state.textAnalysis.main,
  )

  const [anchorElMap, setAnchorElMap] = useState<{
    [key: string]: HTMLElement | null
  }>({})
  const [focusedCard, setFocusedCard] = useState<number | null>(null)
  const [ideasModalOpen, setIdeasModalOpen] = useState(false)
  const cardActionsRefs = useRef<(HTMLDivElement | null)[]>([])
  const isSmallScreen = useMediaQuery('(max-width:1024px)')

  useEffect(() => {
    if (isSmallScreen && Object.keys(anchorElMap).length) {
      setAnchorElMap({})
    }
  }, [dispatch, isSmallScreen])

  const onClickAddTag = useCallback(
    (index: number) => {
      handleAddTagIcon(index, setAnchorElMap)
      dispatch(fetchAllTags())
    },
    [dispatch],
  )

  const onTagDelete: OnDeleteTagProp = useCallback(
    (item, index, tagToRemove) => {
      setFocusedCard(null)
      handleDeleteTag(dispatch, item, index, tagToRemove, 'contributionTags')
    },
    [dispatch],
  )

  const onCheckboxClick = useCallback(
    (item: TAResponseProps) => {
      handleCheckboxClickHelper(item, selectedCards, dispatch)
    },
    [dispatch, selectedCards],
  )

  const onIdeasCommentTitleClick = useCallback(
    (item: TAResponseProps) => {
      const {
        attributes: {
          associations: { parent },
        },
      } = item
      const obj = { entity_id: parent.id }
      dispatch(getSingleContribution(obj))
      setIdeasModalOpen(true)
    },
    [dispatch],
  )

  const renderCardActions = useCallback(
    (index: number) => (
      <CardActions
        index={index}
        anchorElMap={anchorElMap}
        onClickAddTag={() => onClickAddTag(index)}
        $focused={focusedCard === index}
        innerRef={(el: HTMLDivElement | null) =>
          (cardActionsRefs.current[index] = el)
        }
      />
    ),
    [anchorElMap, focusedCard, onClickAddTag],
  )

  const renderTagMenu = useCallback(
    (item: TAResponseProps, index: number) => (
      <TagMenuContainer
        item={item}
        index={index}
        anchorElMap={anchorElMap}
        setAnchorElMap={setAnchorElMap}
        cardActionsRefs={cardActionsRefs.current[index]}
      />
    ),
    [anchorElMap, setAnchorElMap, cardActionsRefs],
  )

  const renderCardTags = useCallback(
    (item: TAResponseProps, index: number) => (
      <ChipsList
        chipData={item?.attributes?.tags || []}
        size="small"
        chipKey="id"
        chipLabel="name"
        variant="outlined"
        chipDelete
        showTooltip
        tooltipText="Delete"
        onChipDelete={(tag: SingleTagProps) => onTagDelete(item, index, tag)}
      />
    ),
    [onTagDelete],
  )

  const onEditSentiment = (item: TAResponseProps, data: Option) => {
    const obj = {
      sentiment: data.value,
      id: item.id,
      source_id: item.attributes.source_id,
    }
    dispatch(editSentiment(obj))
  }

  const renderContributionsList = useMemo(
    () =>
      data?.map((item: TAResponseProps, index: number) => (
        <ActionCard
          key={index}
          id={`action-card-${index + 1}`}
          index={index}
          anchorElMap={anchorElMap}
          showCheckbox={false}
          hover={false}
          showAddTag={false}
          highlightKeyPhrases={item?.attributes?.active_key_phrases}
          keyPhrases={item?.attributes?.key_phrases}
          cardMeta={cardUserAndMeta(item)}
          cardTitle={renderCardTitleContent(item)}
          cardTitleLink={renderCardTitleLink(item)}
          cardContent={renderCardBodyContent(item)}
          cardTagMenu={renderTagMenu(item, index)}
          cardActions={renderCardActions(index)}
          cardTags={renderCardTags(item, index)}
          cardMetrics={renderCardMetricsContent(item)}
          cardSentiment={item.attributes.sentiment}
          onFocus={() => setFocusedCard(index)}
          onBlur={() => setFocusedCard(null)}
          onMouseLeave={() => setFocusedCard(null)}
          $focused={focusedCard === index}
          handleCheckboxClick={() => onCheckboxClick(item)}
          handleIdeasCommentTitleClick={() => onIdeasCommentTitleClick(item)}
          handleEditSentiment={(data) => onEditSentiment(item, data)}
          $checked={isCardSelected(item, selectedCards)}
        />
      )),
    [
      data,
      anchorElMap,
      focusedCard,
      selectedCards,
      onCheckboxClick,
      onIdeasCommentTitleClick,
      onEditSentiment,
    ],
  )

  return (
    <>
      {renderContributionsList}
      <IdeasCommentDialog
        open={ideasModalOpen}
        renderTagMenu={renderTagMenu}
        onClose={() => setIdeasModalOpen(false)}
      />
    </>
  )
}

export { CardList }
